import { Component, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { SettingService } from 'src/app/core/service/setting.service';
import { MemberService } from 'src/app/core/service/member.service';
import { PipesModule } from '../../pipes/pipes.module';
import { TranslateModule } from '@ngx-translate/core';
import { HelperUtils } from 'src/app/core/utility/helper-utils';
import { TeamService } from 'src/app/core/service/team.service';
import { MessageSubject, MessengerService } from 'src/app/core/service/messenger.service';

@Component({
  selector: 'app-patient-header-configurable',
  imports: [PipesModule, TranslateModule, CommonModule],
  templateUrl: './patient-header-configurable.component.html',
  styleUrl: './patient-header-configurable.component.scss',
})
export class PatientHeaderConfigurableComponent implements OnDestroy
{
  @Input() public patientRestricted: boolean;
  @Input() public patientOptedOutAndRestricted: boolean;
  @Input() public compositeRiskScore: number;

  /** Exposed for templates (e.g. `HelperUtils.formatAddressLocalityLine`). */
  public readonly HelperUtils = HelperUtils;

  public sections: any[] = [];
  public loadedMember: any;
  public loadedFields: Map<string, any>;
  public totalAdults: number;
  public totalChildren: number;
  public insurancesText: string;
  public identifiersMap: Map<string, { value: string | null, label: string | null }>;
  public usedSections: number;

  /** Subscriptions tied to the current `member` input (reset when `member` changes or on destroy). */
  private memberSubscriptions = new Subscription();

  private readonly identifierCodes = [
    { code: 'MA', key: 'MEDICAID_NUMBER' },
    { code: 'MC', key: 'MEDICARE_NUMBER' },
    { code: 'SS', key: 'SOCIAL_SECURITY_NUMBER' },
    { code: 'DL', key: 'DRIVER_LICENSE_NUMBER' },
    { code: 'PPN', key: 'PASSPORT_NUMBER' },
    { code: 'NI', key: 'NATIONAL_UNIQUE_INDIVIDUAL' },
    { code: 'XV', key: 'HEALTH_PLAN_IDENTIFIER' },
  ] as const;

  constructor(
    private settingService: SettingService,
    private memberService: MemberService,
    private messengerService: MessengerService,
    private teamService: TeamService)
  { }

  public get member(): any
  {
    return this.loadedMember;
  }

  @Input() public set member(memberData: any)
  {
    this.memberSubscriptions.unsubscribe();
    this.memberSubscriptions = new Subscription();
    this.memberSubscriptions.add(
      this.settingService.getCachedSessionSettings().subscribe({
        next: (sessionSetting) =>
        {
          this.usedSections = 0;
          if (sessionSetting.clientHeaderSection1 != '')
          {
            this.usedSections++;
          }
          if (sessionSetting.clientHeaderSection2 != '')
          {
            this.usedSections++;
          }
          if (sessionSetting.clientHeaderSection3 != '')
          {
            this.usedSections++;
          }

          // Load header sections into an array
          const sections = [];
          for (let i = 1; i <= 3; i++) 
          {
            const settingfields = sessionSetting[`clientHeaderSection${i}`];
            sections.push({ fields: settingfields ? settingfields.split(',') : [] });
          }
          this.sections = sections;

          // Load header sections into a map
          const map = new Map<string, any>();
          this.memberService.clientHeaderSections.forEach(section =>
          {
            map.set(section.value, { text: section.text });
          });
          this.loadedFields = map;

          // Calculate total adults and children
          this.totalAdults = this.getSumValue(memberData?.memberAdults);
          this.totalChildren = this.getSumValue(memberData?.memberChildren);

          // Set identifier values and labels
          const idMap = new Map<string, { value: string | null, label: string | null }>();
          this.identifierCodes.map(({ code, key }) =>
          {
            const id = this.getIdentifierValueAndLabel(memberData, code);
            idMap.set(key, { value: id?.value ?? null, label: id?.label ?? null });
          });
          this.identifiersMap = idMap;

          // Get member insurances
          const insurers = memberData.insurances.map((i: any) => (i.insurer.name));
          this.insurancesText = insurers ? insurers.sort().join(', ') : null;

          this.loadedMember = memberData;
        }
      })
    );
  }

  public ngOnDestroy(): void
  {
    this.memberSubscriptions.unsubscribe();
  }

  /**
   * Identifier header fields are rendered outside `@switch` because consecutive stacked
   * `@case` (shared block) is not accepted inside `ng-template` (NG5002: incomplete `case`).
   */
  public isHeaderIdentifierField(field: string | null | undefined): boolean
  {
    return field != null && this.identifierCodes.some(c => c.key === field);
  }

  public openVideo(openUrl: string): void
  {
    this.messengerService.sendMessage(new MessageSubject('openVideo', { url: openUrl }));
  }

  public getNotificationTypeText(notetificationType: string): string 
  {
    if (!notetificationType)
    {
      return null;
    }
    return this.teamService.notifications.find(x => x.value === notetificationType)?.text ?? null;
  }

  public getOtherNames(
    field: Array<{
      givenName?: string | null;
      familyName?: string | null;
      nameType?: { displayName?: string | null } | null;
    }> | null | undefined): string | null
  {
    if (!field?.length)
    {
      return null;
    }

    const segments = field
      .map((name) =>
      {
        const given = (name?.givenName ?? '').trim();
        const family = (name?.familyName ?? '').trim();
        const namePart = [given, family].filter(Boolean).join(' ');
        if (!namePart)
        {
          return '';
        }
        const typeLabel = (name?.nameType?.displayName ?? '').trim();
        return typeLabel ? `${namePart} (${typeLabel})` : namePart;
      })
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    return segments.length ? segments.join(', ') : null;
  }

  public getDisplayNames(field: Array<{ displayName?: string }> | null | undefined): string | null
  {
    if (!field?.length)
    {
      return null;
    }

    const displayNames = field
      .map((s: any) => (s?.displayName ?? s?.writtenLanguage?.displayName ?? s?.readingLanguage?.displayName ?? '').trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b));

    return displayNames.length ? displayNames.join(', ') : null;
  }

  public getPovertyText(familyIncome: number, povertyLevel: number): string 
  {
    return this.memberService.getPovertyText(familyIncome, povertyLevel);
  }

  public convertPreferredContactMethodsToDisplayTexts(methods: []): string
  {
    if (!methods?.length)
    {
      return null;
    }
    return this.memberService.convertPreferredContactMethodsToDisplayTexts(methods.join(','));
  }

  /** Sums `value` on each entry (e.g. `memberChildren`, `memberAdults`). */
  private getSumValue(property: any): number | null
  {
    if (!property?.length)
    {
      return null;
    }

    let sum = 0;
    for (const item of property)
    {
      const n = Number(item?.value);
      if (!Number.isNaN(n))
      {
        sum += n;
      }
    }
    return sum;
  }

  private getIdentifierValueAndLabel(memberData: any, code: string): any
  {
    const identifier = memberData.identifiers.find((x: any) => x.dictionary.code === code);
    return identifier ? { value: identifier.value, label: identifier.dictionary.displayName } : null;
  }
}
